<template>
  <div class="document-viewer">
    <!-- Standardized title bar -->
    <div class="title-bar">
      <span style="flex: 1">Document Viewer</span>
      <button class="share-button" @click="shareDocument">
        <i class="fas fa-share-alt"></i>
      </button>
      <button class="back-button" @click="goBack">
        <i class="fas fa-arrow-left"></i>
      </button>
      <button v-if="!isMobile" class="home" title="Close" @click="closeViewer">
        <i class="fas fa-close"></i>
      </button>
      <button class="home" title="Home" @click="$router.push('/mhome')">
        <i class="fas fa-home"></i>
      </button>
    </div>

    <div class="pdf-container">
      <div v-if="pdfDoc" class="pdf-viewer" ref="pdfViewer">
        <div class="pdf-controls" v-if="totalPages > 1">
          <button @click="prevPage" :disabled="currentPage === 1">
            Previous
          </button>
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <button @click="nextPage" :disabled="currentPage === totalPages">
            Next
          </button>
        </div>
        <div
          class="canvas-container"
          ref="canvasContainer"
          @mousedown="startDrag"
          @mousemove="onDrag"
          @mouseup="endDrag"
          @mouseleave="endDrag"
          @touchstart="startTouch"
          @touchmove="onTouch"
          @touchend="endTouch"
          :style="{
            cursor: isDragging ? 'grabbing' : 'grab',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            height: '100%',
          }"
        >
          <canvas
            ref="pdfCanvas"
            :style="{
              transform: `scale(${currentScale}) translate(${dragOffset.x / currentScale}px, ${dragOffset.y / currentScale}px)`,
              transformOrigin: 'top left',
            }"
          ></canvas>
        </div>
      </div>
      <div v-else class="pdf-placeholder">Loading PDF...</div>
    </div>

    <!-- Add document navigation buttons with zoom controls in between -->
    <div class="document-navigation">
      <button class="nav-button nav-left" @click="navigateToPrevDoc">
        <i class="fas fa-chevron-left"></i>
      </button>

      <!-- Add zoom controls -->
      <div class="zoom-controls">
        <button class="zoom-button" @click="zoomOut" title="Zoom Out">
          <i class="fas fa-search-minus"></i>
        </button>
        <span class="zoom-level">{{ Math.round(currentScale * 100) }}%</span>
        <button class="zoom-button" @click="zoomIn" title="Zoom In">
          <i class="fas fa-search-plus"></i>
        </button>
        <button class="zoom-button" @click="resetZoom" title="Reset Zoom">
          <i class="fas fa-compress-arrows-alt"></i>
        </button>
      </div>

      <button class="nav-button nav-right" @click="navigateToNextDoc">
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <!-- Footer for messages -->
    <div class="footer-message">
      <span>{{ footerMessage }}</span>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import * as pdfjsLib from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.entry';

// Set the worker source for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

export default {
  name: 'MDocumentViewer',

  data() {
    return {
      documentId: null,
      document: null,
      pdfLoadTask: null,
      pdfDoc: null, // PDF.js document object
      currentPage: 1, // Current page being displayed
      totalPages: 0, // Total number of pages in the PDF
      footerMessage: '',
      isMobile: false,
      // Add these new properties for document navigation
      recentDocIds: [],
      currentDocIndex: -1,
      // Remove unused properties
      showStickyNote: true,
      notePosition: { top: 50, left: 50 },
      isNoteHighlighted: false,
      showOverlay: false,
      noteWasDoubleClicked: false,
      // Add properties for zoom and pan functionality
      currentScale: 1,
      baseScale: 1,
      minScale: 0.5,
      maxScale: 3,
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 },
      lastDragOffset: { x: 0, y: 0 },
    };
  },

  computed: {
    formattedNote() {
      return this.document && this.document.Note
        ? this.document.Note.replace(/\n/g, '<br>')
        : '';
    },
  },

  methods: {
    // Add this new method
    goBack() {
      window.history.back();
    },

    // Add share document method
    shareDocument() {
      if (!this.documentId) {
        this.updateFooterMessage('No document to share');
        return;
      }

      // Determine base URL based on environment
      let baseUrl;
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;

      if (hostname === 'pixiev2' || hostname === 'localhost') {
        // Development environment
        baseUrl = `${protocol}//${hostname}:8080`;
      } else {
        // Production environment
        baseUrl = 'https://pixie.bigtapapp.com';
      }

      // Construct the full URL
      const shareUrl = `${baseUrl}/mdocs?search=:${this.documentId}`;

      // Try to copy to clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(shareUrl)
          .then(() => {
            this.updateFooterMessage('URL copied to clipboard!');
          })
          .catch((err) => {
            console.error('Could not copy text: ', err);
            this.updateFooterMessage('Failed to copy URL: ' + shareUrl);
          });
      } else {
        // Fallback for browsers that don't support Clipboard API
        this.updateFooterMessage('Copy this URL manually: ' + shareUrl);
      }
    },

    // Load PDF for the document
    async loadPdf() {
      if (!this.documentId) return;

      if (this.pdfLoadTask) {
        try {
          await this.pdfLoadTask.destroy();
        } catch {
          /* ignore */
        }
        this.pdfLoadTask = null;
      }

      try {
        const token = localStorage.getItem('token');
        this.pdfLoadTask = pdfjsLib.getDocument({
          url: `/api/load-pdf/${this.documentId}`,
          httpHeaders: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
        const pdf = await this.pdfLoadTask.promise;
        this.pdfDoc = pdf;
        this.totalPages = pdf.numPages;
        this.resetZoomAndPan();
        await this.renderPage(this.currentPage);
        this.updateFooterMessage(
          `PDF for document ID ${this.documentId} loaded.`,
        );
      } catch (error) {
        if (error?.name === 'AbortException' || error?.message === 'Worker was destroyed') {
          return;
        }
        console.error('Error loading PDF:', error);
        const timedOut =
          error.code === 'ECONNABORTED' ||
          error.status === 504 ||
          /timeout/i.test(error.message || '');
        this.updateFooterMessage(
          timedOut
            ? 'PDF load timed out — document storage may be unavailable.'
            : 'Failed to load PDF.',
        );
      }
    },

    // Reset zoom and pan values
    resetZoomAndPan() {
      this.currentScale = 1;
      this.baseScale = 1;
      this.dragOffset = { x: 0, y: 0 };
      this.lastDragOffset = { x: 0, y: 0 };
    },

    // Render a specific page
    async renderPage(pageNum) {
      const page = await this.pdfDoc.getPage(pageNum);
      const canvas = this.$refs.pdfCanvas;
      const context = canvas.getContext('2d');

      // Calculate the scale based on the container width
      const containerWidth = this.$refs.pdfViewer.clientWidth;
      const viewport = page.getViewport({ scale: 1.0 });
      this.baseScale = containerWidth / viewport.width;

      // Use the baseScale only (not currentScale) since we'll apply currentScale via CSS transform
      const scaledViewport = page.getViewport({ scale: this.baseScale });

      // Set canvas dimensions
      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;

      // Render the PDF page into the canvas
      await page.render({
        canvasContext: context,
        viewport: scaledViewport,
      }).promise;
    },

    // Zoom in functionality
    zoomIn() {
      if (this.currentScale < this.maxScale) {
        this.currentScale += 0.25;
        this.renderPage(this.currentPage);
        this.updateFooterMessage(
          `Zoom: ${Math.round(this.currentScale * 100)}%`,
        );
      }
    },

    // Zoom out functionality
    zoomOut() {
      if (this.currentScale > this.minScale) {
        this.currentScale -= 0.25;
        this.renderPage(this.currentPage);
        this.updateFooterMessage(
          `Zoom: ${Math.round(this.currentScale * 100)}%`,
        );
      }
    },

    // Reset zoom to original
    resetZoom() {
      this.currentScale = 1;
      this.dragOffset = { x: 0, y: 0 };
      this.lastDragOffset = { x: 0, y: 0 };
      this.renderPage(this.currentPage);
      this.updateFooterMessage('Zoom reset to 100%');
    },

    // Start dragging (mouse)
    startDrag(event) {
      this.isDragging = true;
      this.dragStart = {
        x: event.clientX,
        y: event.clientY,
      };
    },

    // During drag (mouse)
    onDrag(event) {
      if (this.isDragging) {
        const dx = event.clientX - this.dragStart.x;
        const dy = event.clientY - this.dragStart.y;

        this.dragOffset = {
          x: this.lastDragOffset.x + dx,
          y: this.lastDragOffset.y + dy,
        };
      }
    },

    // End dragging (mouse)
    endDrag() {
      if (this.isDragging) {
        this.lastDragOffset = { ...this.dragOffset };
        this.isDragging = false;
      }
    },

    // Touch event handlers for mobile
    startTouch(event) {
      // Prevent default to avoid scrolling the page
      event.preventDefault();

      this.isDragging = true;

      // Get touch coordinates
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        this.dragStart = {
          x: touch.clientX,
          y: touch.clientY,
        };
      }
    },

    onTouch(event) {
      // Prevent default to avoid scrolling the page
      event.preventDefault();

      if (this.isDragging && event.touches.length === 1) {
        const touch = event.touches[0];
        const dx = touch.clientX - this.dragStart.x;
        const dy = touch.clientY - this.dragStart.y;

        this.dragOffset = {
          x: this.lastDragOffset.x + dx,
          y: this.lastDragOffset.y + dy,
        };
      }
    },

    endTouch() {
      // Make sure we update our last offset even if the user
      // moves their finger off the element
      if (this.isDragging) {
        this.lastDragOffset = { ...this.dragOffset };
        this.isDragging = false;
      }
    },

    // Navigate to the previous page
    async prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.renderPage(this.currentPage);
      }
    },

    // Navigate to the next page
    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.renderPage(this.currentPage);
      }
    },

    // Update the footer message
    updateFooterMessage(message) {
      this.footerMessage = message;

      // Clear message after 10 seconds
      setTimeout(() => {
        if (this.footerMessage === message) {
          this.footerMessage = '';
        }
      }, 10000);
    },

    // Parse URL parameters to get document ID
    parseUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const searchParam = urlParams.get('search');

      if (searchParam && searchParam.startsWith(':')) {
        const docId = parseInt(searchParam.substring(1), 10);
        if (!isNaN(docId)) {
          this.documentId = docId;
          return;
        }
      }

      this.updateFooterMessage(
        'No valid document ID provided. Format should be search=:DOCID',
      );
    },

    closeViewer() {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        this.$router.push('/mhome');
      }
    },

    checkMobile() {
      this.isMobile = window.innerWidth <= 500;
    },

    // Fetch recent document IDs
    async fetchRecentDocIds() {
      try {
        const response = await axios.get('/api/get-recent-document-ids');
        if (response.status === 200 && response.data.length > 0) {
          this.recentDocIds = response.data.map((doc) => doc.id);

          // Find current document's position in the array
          if (this.documentId) {
            this.currentDocIndex = this.recentDocIds.findIndex(
              (id) => id === parseInt(this.documentId, 10),
            );
          } else {
            // If no document ID was supplied, default to the most recent
            this.currentDocIndex = 0;
            this.documentId = this.recentDocIds[0];
            this.loadPdf();
          }
        }
      } catch (error) {
        console.error('Error fetching recent document IDs:', error);
        this.updateFooterMessage('Failed to load document list');
      }
    },

    // Navigate to the previous document
    navigateToPrevDoc() {
      if (this.recentDocIds.length === 0) return;

      // Move to previous index with loop-around
      this.currentDocIndex--;
      if (this.currentDocIndex < 0) {
        this.currentDocIndex = this.recentDocIds.length - 1; // Loop to the end
      }

      // Load the new document
      this.documentId = this.recentDocIds[this.currentDocIndex];
      this.loadPdf();
      this.updateFooterMessage(`Navigated to document #${this.documentId}`);
    },

    // Navigate to the next document
    navigateToNextDoc() {
      if (this.recentDocIds.length === 0) return;

      // Move to next index with loop-around
      this.currentDocIndex++;
      if (this.currentDocIndex >= this.recentDocIds.length) {
        this.currentDocIndex = 0; // Loop back to start
      }

      // Load the new document
      this.documentId = this.recentDocIds[this.currentDocIndex];
      this.loadPdf();
      this.updateFooterMessage(`Navigated to document #${this.documentId}`);
    },
  },

  mounted() {
    this.isMobile = window.innerWidth <= 500;
    window.addEventListener('resize', this.checkMobile);

    // Parse URL parameters
    this.parseUrlParams();

    // Fetch recent document IDs
    this.fetchRecentDocIds();

    if (this.$route?.params?.id) {
      this.documentId = parseInt(this.$route.params.id, 10);
    }
    if (this.documentId) this.loadPdf();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
    if (this.pdfLoadTask) {
      try {
        this.pdfLoadTask.destroy();
      } catch {
        /* ignore */
      }
    }
  },
};
</script>

<style scoped>
.document-viewer {
  width: 100%;
  max-width: 420px;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.title-bar {
  background: #a080a8;
  color: #fff;
  font-size: 1.4em;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  margin-bottom: 10px;
}

.back-button {
  width: 30px;
  height: 30px;
  border-radius: 18px;
  border: 1px solid #e1dede;
  font-weight: normal;
  font-size: 0.6em;
  color: #eee;
  margin-right: 5px;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home {
  height: 30px;
  width: 30px;
  font-size: 0.6em;
  border-radius: 15px;
  color: #fff;
  background: #907793;
  margin-right: 10px;
  border: 1px solid #ccc;
}

.share-button {
  width: 30px;
  background: #907793;
  height: 30px;
  border-radius: 18px;
  border: 1px solid #e1dede;
  font-weight: normal;
  font-size: 0.6em;
  color: #eee;
  margin-right: 8px;
}

/* Fix for FontAwesome icons in buttons */
.title-bar button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.pdf-container {
  width: 100%;
  height: calc(100vh - 120px);
  position: relative;
  overflow: hidden; /* Changed from auto to hidden to manage scrolling with dragging */
}

.pdf-viewer {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pdf-viewer canvas {
  width: 100%;
  height: auto;
  touch-action: none; /* Prevent browser's default touch actions */
}

.pdf-controls {
  margin: 10px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.pdf-controls button {
  padding: 5px 10px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.pdf-controls button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.pdf-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: #f5f5f5;
  border: 1pt solid #ddd;
  border-radius: 4px;
  font-size: 1.2em;
  color: #666;
}

.document-navigation {
  position: fixed;
  bottom: 70px;
  width: 92%;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 100;
  align-items: center;
}

.nav-button {
  width: 35px;
  height: 35px;
  border-radius: 23px;
  border: 1px solid #a9a5a5;
  background-color: white;
  color: #a080a8;
  font-size: 1.2em;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.nav-button:hover {
  background-color: #f0f0f0;
}

.nav-button:active {
  transform: translateY(1px);
}

.nav-left {
  left: 20px;
}

.nav-right {
  right: 20px;
}

/* Zoom controls styling */
.zoom-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: white;
  padding: 4px 10px;
  border-radius: 20px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.zoom-button {
  width: 28px;
  height: 28px;
  border-radius: 14px;
  border: 1px solid #a9a5a5;
  background-color: white;
  color: #a080a8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.zoom-button:hover {
  background-color: #f0f0f0;
}

.zoom-button:active {
  transform: translateY(1px);
}

.zoom-level {
  font-size: 0.85em;
  font-weight: bold;
  color: #333;
  width: 40px;
  text-align: center;
}

.canvas-container {
  width: 100%;
  height: calc(100% - 50px); /* Adjust if pdf-controls are shown */
  touch-action: none; /* Prevent browser's default touch actions */
}

/* Adjust the footer padding to make room for nav buttons */
.footer-message {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 20px;
  background-color: #f5f5f5;
  border-top: 1px solid #ddd;
  padding: 7px;
  padding-left: 20px;
  font-size: 0.8em;
  z-index: 1000;
  padding-bottom: 25px;
}

/* Mobile-specific adjustments */
@media (max-width: 500px) {
  .pdf-container {
    height: calc(100vh - 150px);
    width: 100% !important;
    overflow: hidden !important; /* Changed from auto to hidden */
  }

  .pdf-viewer {
    width: 100% !important;
  }

  .zoom-controls {
    padding: 2px 8px;
  }

  .zoom-button {
    width: 24px;
    height: 24px;
  }

  .zoom-level {
    width: 35px;
    font-size: 0.75em;
  }
}
</style>
